import { Tooltip, styled } from '@mui/material'
import { Text } from '../../../../components/typography'
import { colorPalette } from '../../../../style/colorPalette'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { intl } from '../../../../../i18n'
import { GeneratedSprint } from './generateSprints'

type Props = {
  sprints: GeneratedSprint[]
  showCode: boolean
  emptyMessage?: string
  onShift: (index: number, direction: 1 | -1) => void
}

const SprintPreviewTable = ({
  sprints,
  showCode,
  emptyMessage,
  onShift,
}: Props) => (
  <PreviewSection>
    <Text variant="sBold">
      {intl.formatMessage({ id: 'bulkSprint.preview' })}
    </Text>
    <PreviewScrollArea>
      <PreviewTable>
        <thead>
          <PreviewHeaderRow>
            {showCode && (
              <PreviewTh>
                <Text variant="2sBold">
                  {intl.formatMessage({
                    id: 'bulkSprint.preview.code',
                  })}
                </Text>
              </PreviewTh>
            )}
            <PreviewTh>
              <Text variant="2sBold">
                {intl.formatMessage({
                  id: 'bulkSprint.preview.name',
                })}
              </Text>
            </PreviewTh>
            <PreviewTh>
              <Text variant="2sBold">
                {intl.formatMessage({
                  id: 'bulkSprint.preview.startDate',
                })}
              </Text>
            </PreviewTh>
            <PreviewTh>
              <Text variant="2sBold">
                {intl.formatMessage({
                  id: 'bulkSprint.preview.endDate',
                })}
              </Text>
            </PreviewTh>
            <PreviewTh style={{ width: '80px' }}>
              <Text variant="2sBold">
                {intl.formatMessage({
                  id: 'bulkSprint.preview.adjust',
                })}
              </Text>
            </PreviewTh>
          </PreviewHeaderRow>
        </thead>
        <tbody>
          {sprints.map((sprint, i) => (
            <PreviewRow key={sprint.name}>
              {showCode && (
                <PreviewTd>
                  <Text variant="2s">{sprint.code}</Text>
                </PreviewTd>
              )}
              <PreviewTd>
                <Text variant="2s">{sprint.name}</Text>
              </PreviewTd>
              <PreviewTd>
                <Text variant="2s">
                  {dateV2Service.formatWithWeekday(sprint.startDate)}
                </Text>
              </PreviewTd>
              <PreviewTd>
                <Text variant="2s">
                  {dateV2Service.formatWithWeekday(sprint.endDate)}
                </Text>
              </PreviewTd>
              <PreviewTd>
                <ShiftButtonGroup>
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'bulkSprint.shift.backward',
                    })}
                    disableInteractive={true}
                    slotProps={{
                      tooltip: { sx: { whiteSpace: 'pre-line' } },
                      popper: { sx: { pointerEvents: 'none' } },
                    }}
                  >
                    <ShiftButton onClick={() => onShift(i, -1)}>
                      −1w
                    </ShiftButton>
                  </Tooltip>
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'bulkSprint.shift.forward',
                    })}
                    disableInteractive={true}
                    slotProps={{
                      tooltip: { sx: { whiteSpace: 'pre-line' } },
                      popper: { sx: { pointerEvents: 'none' } },
                    }}
                  >
                    <ShiftButton onClick={() => onShift(i, 1)}>+1w</ShiftButton>
                  </Tooltip>
                </ShiftButtonGroup>
              </PreviewTd>
            </PreviewRow>
          ))}
        </tbody>
      </PreviewTable>
      {emptyMessage && (
        <EmptyMessage>
          <Text variant="s">{emptyMessage}</Text>
        </EmptyMessage>
      )}
    </PreviewScrollArea>
  </PreviewSection>
)

const PreviewSection = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 24px 0',
})

const PreviewScrollArea = styled('div')({
  marginTop: '8px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
})

const PreviewTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '6px',
  overflow: 'hidden',
})

const PreviewHeaderRow = styled('tr')({
  backgroundColor: colorPalette.skyBlue[1],
  borderBottom: `2px solid ${colorPalette.skyBlue[2]}`,
})

const PreviewTh = styled('th')({
  textAlign: 'left',
  padding: '6px 8px',
  position: 'sticky',
  top: 0,
  backgroundColor: colorPalette.skyBlue[1],
  zIndex: 1,
  color: colorPalette.skyBlue[10],
})

const PreviewRow = styled('tr')({
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
  '&:nth-of-type(odd)': {
    backgroundColor: colorPalette.skyBlue[0],
  },
})

const PreviewTd = styled('td')({
  padding: '4px 8px',
})

const ShiftButtonGroup = styled('div')({
  display: 'flex',
  gap: '4px',
})

const ShiftButton = styled('button')({
  border: `1px solid ${colorPalette.skyBlue[2]}`,
  borderRadius: '4px',
  backgroundColor: colorPalette.monotone[0],
  padding: '2px 6px',
  fontSize: '11px',
  cursor: 'pointer',
  color: colorPalette.skyBlue[7],
  fontWeight: 600,
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[0],
    borderColor: colorPalette.skyBlue[5],
  },
})

const EmptyMessage = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  color: colorPalette.monotone[3],
})

export { SprintPreviewTable }
